// Angular
import {
  ApplicationConfig,
  DEFAULT_CURRENCY_CODE,
  inject,
  LOCALE_ID,
  provideAppInitializer,
  provideZoneChangeDetection,
} from '@angular/core';
import {
  provideRouter,
  withComponentInputBinding,
  withDisabledInitialNavigation,
  withRouterConfig,
} from '@angular/router';
import { provideHttpClient, withInterceptors } from '@angular/common/http';

// External Packages
import { provideAngularSvgIcon } from 'angular-svg-icon';
import { provideAuth, withAppInitializerAuthCheck } from 'angular-auth-oidc-client';
import { Settings as LuxonSettings } from 'luxon';
import { filter, take } from 'rxjs';

// Pin Luxon to en-IE so date formatting and helpers like Info.getStartOfWeek()
// always return Irish conventions (week starts Monday), regardless of the
// user's browser locale. Matches the Angular LOCALE_ID provider below.
LuxonSettings.defaultLocale = 'en-IE';

// Auth
import { authInterceptor } from './core/auth/interceptor/auth.interceptor';
import { authConfig } from './core/auth/config/auth.config';
import { GlobalAuthService } from './core/auth/services/global-auth.service';
import { FeatureFlagService } from './core/feature-flags/feature-flag.service';

// Routes
import { routes } from './app.routes';

export const appConfig: ApplicationConfig = {
  providers: [
    provideAuth(authConfig, withAppInitializerAuthCheck()),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(
      routes,
      withComponentInputBinding(),
      withRouterConfig({ onSameUrlNavigation: 'reload' }),
      withDisabledInitialNavigation(),
    ),
    provideHttpClient(withInterceptors([authInterceptor])),
    provideAppInitializer(() => {
      const auth = inject(GlobalAuthService);
      const flags = inject(FeatureFlagService);
      auth
        .getIsAuthenticated()
        .pipe(
          filter((isAuthenticated) => isAuthenticated),
          take(1),
        )
        .subscribe(() => {
          flags.load().catch((error) => console.error('[feature-flags] initial load failed:', error));
        });
    }),
    provideAngularSvgIcon(),
    { provide: DEFAULT_CURRENCY_CODE, useValue: 'EUR' },
    { provide: LOCALE_ID, useValue: 'en-IE' },
  ],
};
